// ============================================
// ボスエンティティの生成と攻撃パターン
// ============================================

import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';
import { ENEMY_CONFIG } from '../../config/enemy-config';
import {
    getWave,
    getLanes,
    getEnemies,
    setBossWarning,
    setBossWarningTimer,
    setBossName,
    getEnemyBullets,
    getPlayer,
    getBossPaused
} from '../../store/index';
import { getDiffConfig } from '../../utils/helpers';
import type { Enemy, EnemyBullet, BossEnemy } from '../../types/game-types';

function isTick(timer: number, interval: number): boolean {
    return Math.floor(timer % interval) < Math.floor((timer - 1) % interval);
}

/**
 * updateBoss() - ボスの更新
 */
export function updateBoss(enemy: BossEnemy, dt: number): void {
    // リスポーン待機中はボスの全動作を停止
    if (getBossPaused()) {
        return;
    }

    const p = getP5();
    const player = getPlayer();
    if (!player) {
        return;
    }

    const lanes = getLanes();
    const wave = getWave();

    // 出現直後の移動（滞在位置に到達するまで）
    if (!enemy.isStaying && !enemy.isDescending) {
        enemy.y += 1.5 * dt; // 下降速度
        if (enemy.y >= ENEMY_CONFIG.boss.stayPositionY) {
            enemy.y = ENEMY_CONFIG.boss.stayPositionY;
            enemy.isStaying = true;
            enemy.stayTimer = 0;
        }
    }

    // 上部滞在中
    if (enemy.isStaying && (enemy.stayTimer ?? 0) < ENEMY_CONFIG.boss.stayDuration) {
        enemy.stayTimer = (enemy.stayTimer ?? 0) + dt;
        enemy.y = ENEMY_CONFIG.boss.stayPositionY;
        enemy.phaseTimer = (enemy.phaseTimer ?? 0) + dt;
        if (enemy.phaseTimer > (enemy.laneChangeInterval ?? 0)) {
            const moveOptions = [player.lane, enemy.lane === 0 ? 1 : 0, enemy.lane === 0 ? 1 : 0];
            enemy.lane = moveOptions[p.floor(p.random(moveOptions.length))];
            enemy.phaseTimer = 0;
        }
        const targetX = lanes[enemy.lane];
        const bossLerpFactor = 1 - Math.pow(1 - 0.05, dt);
        enemy.x = p.lerp(enemy.x, targetX, bossLerpFactor);
    }

    if (enemy.isStaying && (enemy.stayTimer ?? 0) >= ENEMY_CONFIG.boss.stayDuration && !enemy.isDescending) {
        enemy.isStaying = false;
        enemy.isDescending = true;
        enemy.baseX = enemy.x;
        enemy.descentState = 'moving';
        enemy.descentTargetY = enemy.y + ENEMY_CONFIG.boss.descentDistance;
    }

    if (enemy.isDescending) {
        enemy.zigzagPhase = (enemy.zigzagPhase ?? 0) + ENEMY_CONFIG.boss.zigzagSpeed * dt;
        const zigzagOffset = p.sin(enemy.zigzagPhase) * ENEMY_CONFIG.boss.zigzagAmplitude;
        enemy.baseX = p.lerp(enemy.baseX ?? enemy.x, player.x, ENEMY_CONFIG.boss.playerTrackRate * dt);
        const targetX = p.constrain(enemy.baseX + zigzagOffset, 50, p.width - 50);
        const descendLerpFactor = 1 - Math.pow(1 - 0.08, dt);
        enemy.x = p.lerp(enemy.x, targetX, descendLerpFactor);

        if (enemy.descentState === 'moving') {
            enemy.y += ENEMY_CONFIG.boss.descentSpeed * dt;
            if (enemy.y >= (enemy.descentTargetY ?? 0)) {
                enemy.y = enemy.descentTargetY ?? 0;
                enemy.descentState = 'paused';
                enemy.descentPauseTimer = 0;
            }
        } else if (enemy.descentState === 'paused') {
            enemy.descentPauseTimer = (enemy.descentPauseTimer ?? 0) + dt;
            if (enemy.descentPauseTimer >= ENEMY_CONFIG.boss.descentPauseDuration) {
                enemy.descentState = 'moving';
                enemy.descentTargetY = enemy.y + ENEMY_CONFIG.boss.descentDistance;
            }
        }
    }

    // 攻撃パターン
    enemy.attackTimer = (enemy.attackTimer ?? 0) + dt;
    const hpRatio = enemy.hp / enemy.maxHp;
    const atkCfg = ENEMY_CONFIG.boss.attack;
    const bossAppearance = p.floor(wave / ENEMY_CONFIG.boss.spawnWave);
    const intervalMult = p.max(0.4, 1 - (bossAppearance - 1) * 0.15);

    // パターン1: 直線弾（常時）
    if (isTick(enemy.attackTimer ?? 0, atkCfg.straightInterval * intervalMult)) {
        bossShootStraight(enemy);
    }

    // パターン2: 拡散弾
    const spreadThreshold = bossAppearance >= 3 ? 1.0 : bossAppearance >= 2 ? 0.7 : 0.5;
    if (hpRatio < spreadThreshold && isTick(enemy.attackTimer ?? 0, atkCfg.spreadInterval * intervalMult)) {
        bossShootSpread(enemy);
    }

    // パターン3: 追尾弾
    const homingThreshold = bossAppearance >= 4 ? 1.0 : bossAppearance >= 3 ? 0.7 : bossAppearance >= 2 ? 0.5 : 0;
    if (homingThreshold > 0 && hpRatio < homingThreshold && isTick(enemy.attackTimer ?? 0, atkCfg.homingInterval * intervalMult)) {
        bossShootHoming(enemy);
    }

    // パターン4: 弾幕（発狂モード）
    const barrageThreshold = bossAppearance >= 5 ? 0.6 : bossAppearance >= 4 ? 0.5 : bossAppearance >= 3 ? 0.3 : 0;
    if (barrageThreshold > 0 && hpRatio < barrageThreshold && isTick(enemy.attackTimer ?? 0, atkCfg.barrageInterval * intervalMult)) {
        bossShootBarrage(enemy);
    }
}

/**
 * ボス敵を生成します。
 * 特定のウェーブで出現する強力な敵です。
 */
function spawnBoss(): void {
    const p = getP5();
    const wave = getWave();
    const lanes = getLanes();
    const enemies = getEnemies();

    const lane = p.floor(p.random(CONFIG.laneCount));
    const cfg = ENEMY_CONFIG.boss;
    const diffCfg = getDiffConfig();

    // HPはウェーブに応じて増加 + 難易度補正
    const bossHp = p.ceil((cfg.baseHp + wave * cfg.hpPerWave) * diffCfg.bossHpMult);

    const boss: BossEnemy = {
        x: lanes[lane],
        y: -100,
        lane: lane,
        size: cfg.size,
        hp: bossHp,
        maxHp: bossHp,
        type: 'boss',
        color: p.color(cfg.color[0], cfg.color[1], cfg.color[2]),
        rotation: 0,
        rotSpeed: cfg.rotSpeed,
        speedMult: cfg.speedMult,
        score: cfg.score,
        phase: 0,
        phaseTimer: 0,
        attackTimer: 0,
        laneChangeInterval: cfg.laneChangeInterval,
        isBoss: true,
        stayTimer: 0,              // 上部滞在タイマー
        isStaying: false,          // 上部滞在中フラグ
        isDescending: false,       // 下降フェーズフラグ
        descentState: 'moving',    // 下降状態: 'moving' or 'paused'
        descentPauseTimer: 0,      // 下降停止タイマー
        descentTargetY: 0,         // 次の停止目標Y座標
        zigzagPhase: p.random(p.TWO_PI), // ジグザグの初期位相（ランダム）
        baseX: p.width / 2,           // 下降時の基準X座標
        innerColor: p.color(cfg.innerColor[0], cfg.innerColor[1], cfg.innerColor[2]),
        moveTimer: 0,
        moveDir: 0,
        shootTimer: 0,
        patternTimer: 0,
        currentPattern: ''
    };

    enemies.push(boss);

    // ボス名を設定（出現回数に応じた名称）
    const bossAppearance = Math.floor(wave / (ENEMY_CONFIG.boss.spawnWave as number));
    const bossNames = ['VANGUARD', 'SENTINEL', 'OVERLORD', 'EXECUTOR', 'NEMESIS'];
    const nameIndex = Math.min(bossAppearance - 1, bossNames.length - 1);
    setBossName(bossNames[Math.max(0, nameIndex)]);

    // ボス出現メッセージ
    setBossWarning(true);
    setBossWarningTimer(ENEMY_CONFIG.boss.warningDuration);
}

// ============================================
// ボスの攻撃パターン関数
// ============================================

// 直線弾 - プレイヤーに向かって真っ直ぐ
function bossShootStraight(boss: Enemy): void {
    const p = getP5();
    const player = getPlayer();
    if (!player) {
        return;
    }

    const enemyBullets = getEnemyBullets();

    const angle = p.atan2(player.y - boss.y, player.x - boss.x);
    enemyBullets.push({
        x: boss.x,
        y: boss.y + boss.size / 2,
        vx: p.cos(angle) * 5,
        vy: p.sin(angle) * 5,
        size: 12,
        color: p.color(255, 100, 0),
        speed: 5
    } as EnemyBullet);
}

// 拡散弾 - 扇状に複数発
function bossShootSpread(boss: Enemy): void {
    const p = getP5();
    const player = getPlayer();
    if (!player) {
        return;
    }

    const enemyBullets = getEnemyBullets();

    const baseAngle = p.atan2(player.y - boss.y, player.x - boss.x);
    const spreadCount = ENEMY_CONFIG.boss.attack.spreadCount;
    const spreadAngle = p.PI / 6; // 30度の範囲

    for (let i = 0; i < spreadCount; i++) {
        const angle = baseAngle - spreadAngle / 2 + (spreadAngle / (spreadCount - 1)) * i;
        enemyBullets.push({
            x: boss.x,
            y: boss.y + boss.size / 2,
            vx: p.cos(angle) * 4,
            vy: p.sin(angle) * 4,
            size: 10,
            color: p.color(255, 50, 150),
            speed: 4
        } as EnemyBullet);
    }
}

// 追尾弾 - プレイヤーを追いかける
function bossShootHoming(boss: Enemy): void {
    const p = getP5();
    const player = getPlayer();
    if (!player) {
        return;
    }

    const enemyBullets = getEnemyBullets();

    const angle = p.atan2(player.y - boss.y, player.x - boss.x);
    enemyBullets.push({
        x: boss.x,
        y: boss.y + boss.size / 2,
        vx: p.cos(angle) * 3,
        vy: p.sin(angle) * 3,
        size: 15,
        color: p.color(150, 0, 255),
        homing: true,
        speed: 3
    } as EnemyBullet);
}

// 弾幕 - 円形に大量発射（発狂モード）
function bossShootBarrage(boss: Enemy): void {
    const p = getP5();
    const enemyBullets = getEnemyBullets();

    const bulletCount = ENEMY_CONFIG.boss.attack.barrageCount;
    const startAngle = (p.frameCount * 0.1) % p.TWO_PI; // 回転する弾幕

    for (let i = 0; i < bulletCount; i++) {
        const angle = startAngle + (p.TWO_PI / bulletCount) * i;
        enemyBullets.push({
            x: boss.x,
            y: boss.y,
            vx: p.cos(angle) * 3,
            vy: p.sin(angle) * 3,
            size: 8,
            color: p.color(255, 255, 0),
            speed: 3
        } as EnemyBullet);
    }
}

export { spawnBoss, bossShootStraight, bossShootSpread, bossShootHoming, bossShootBarrage };
